import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import {
    formatNarration,
    formatNarrationWindow,
    formatSceneNarration,
    narrationWindowAt,
    type TimedWord,
    timeNarrationWords,
} from '../html-design/sceneNarration';
import { MediaRegistry } from '../registries/MediaRegistry';
import {
    captureSceneFrames,
    captureSceneScreenshot,
    CAPTURE_MODE_PROGRESS,
    SCREENSHOT_MAX_EDGE,
} from '../rendering/sceneScreenshot';
import {
    type EnhancedToolResult,
    type ImageContent,
    Model,
    ReasoningEffort,
    type TextContent,
    type ToolExecutionContext,
} from '../types';

import { ConversationTool } from './ConversationTool';
import { reviewSkippedResult } from './review-skipping';
import { resolveSceneById, sceneDisplayId } from './scene-addressing';
import { getSceneTranscriptTokens } from './transcript-partition';

import { AssetContainerTypes, SlideBackgroundModelTypeEnum } from 'js/src/client';
import { enterCreationStage, isReviewSkipped, setSceneReviewing } from 'js/src/data/agent-activity';
import { INVISIBLE_IMAGE_SOURCE_DATA } from 'js/src/libs/consts';

export const imageAskSchema = z.object({
    media_ids: z
        .array(z.string())
        .optional()
        .describe('IDs of media from the media registry to include as images.'),
    scene_ids: z
        .array(z.string())
        .optional()
        .describe('scene_ids (from the video state) of scenes to render and include as images.'),
    question: z
        .string()
        .describe(
            'A detailed question to ask about the provided image(s). Be specific about what you want to know. When referring to scenes, use their scene_id (e.g. "scene a1b2c3") to match the image labels.',
        ),
    detail: z
        .enum(['low', 'high'])
        .describe(
            "Screenshot detail. 'low' (default) is a cheap overview — enough " +
                "to see layout and colors, but too small to read body text. Use 'high' " +
                'whenever the question is about the copy itself or about whether an ' +
                'element actually rendered.',
        )
        .optional(),
    capture_mode: z
        .enum(['midpoint', 'timeline_scan'])
        .describe(
            "How to capture each scene. 'midpoint' is a single frame from the " +
                'middle of the scene, which is the representative still for a scene ' +
                "whose content simply sits there. 'timeline_scan' spreads several " +
                'frames across the whole scene, so the question can be answered about ' +
                'motion rather than about one instant — use it for anything animated, ' +
                'a graphic you authored most of all. Omit for the scene preview frame.',
        )
        .optional(),
    progress: z
        .number()
        .describe(
            'Render one still at this exact point instead, 0 (start) to 1 ' +
                '(end) — for an instant no preset covers, such as the onset of a ' +
                'word you are syncing to. At 0 nothing has animated in yet, so an ' +
                'empty frame there is expected rather than a fault.',
        )
        .optional(),
});
export type ImageAskArgs = z.infer<typeof imageAskSchema>;

type ResolvedImage = { url: string } | { error: string };
type ResolvedScene = { sceneId: string; sceneIndex: number };

/**
 * Returns a text answer only, never the frames: the judgement is meant to come from a model with no
 * stake in the work, so there is deliberately no mode that hands them back to the caller.
 */
export class ImageAskTool extends ConversationTool<typeof imageAskSchema> {
    public override canRunAsync = true;
    public override readonly childModel = Model.GPT_5_6_LUNA;
    public override readonly childReasoningEffort = ReasoningEffort.LOW;

    protected override readonly schema = imageAskSchema;
    protected override readonly toolName = TOOL_NAMES.imageAsk;
    protected override readonly toolDescription =
        'Ask a vision model a detailed question about one or more images and get back a text ' +
        'answer. The images never enter your own context, so the question has to carry what you ' +
        'need to know. Media and scenes can go in one call, to ask how a piece of media sits ' +
        'inside the scene using it. This is how you review a scene, most of all a graphic you ' +
        `just wrote with ${TOOL_NAMES.writeHtmlAsset}.`;

    public override getToolDisplayName(): string {
        return 'Reviewing scene';
    }

    public get statusIcon(): string {
        return 'image_search';
    }

    public async execute(
        args: ImageAskArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        if (isReviewSkipped(this.getState())) {
            return { result: reviewSkippedResult(), isSuccess: true };
        }

        // Announced up front, not off the per-scene reviewing bracket below: that bracket
        // is empty for a media-only ask, and it lifts between batches of scenes, so it
        // can't say "the review phase is underway".
        this.dispatch(enterCreationStage({ stage: 'reviewing' }));

        const mediaIds = args.media_ids ?? [];
        const sceneIds = args.scene_ids ?? [];

        if (mediaIds.length === 0 && sceneIds.length === 0) {
            return {
                result: 'Error: provide at least one media id (media_ids) or scene id (scene_ids) to ask about.',
                isSuccess: false,
            };
        }

        const resolvedScenes: ResolvedScene[] = [];
        for (const sceneId of sceneIds) {
            const resolved = resolveSceneById(this.luminary, this.getState(), sceneId);
            if ('error' in resolved) {
                return { result: `Error: ${resolved.error}`, isSuccess: false };
            }
            resolvedScenes.push({ sceneId, sceneIndex: resolved.sceneIndex });
        }

        const reviewingSceneIds = resolvedScenes.map(
            ({ sceneIndex }) => this.luminary.scenes[sceneIndex].id,
        );
        if (reviewingSceneIds.length > 0) {
            this.dispatch(setSceneReviewing({ sceneIds: reviewingSceneIds, isReviewing: true }));
            // A reviewed scene is considered filled — clear its build-preview blur
            // right away (on its own, no need to wait for both text and media)
            // rather than after the vision call returns.
            this.recordSceneFillForScenes(
                resolvedScenes.map(({ sceneIndex }) => sceneIndex),
                'review',
            );
        }
        try {
            return await this.askAboutImages(args, context, mediaIds, resolvedScenes);
        } finally {
            if (reviewingSceneIds.length > 0) {
                this.dispatch(
                    setSceneReviewing({ sceneIds: reviewingSceneIds, isReviewing: false }),
                );
            }
        }
    }

    private async askAboutImages(
        args: ImageAskArgs,
        context: ToolExecutionContext,
        mediaIds: string[],
        resolvedScenes: ResolvedScene[],
    ): Promise<EnhancedToolResult> {
        const { question } = args;
        const messageContent: (TextContent | ImageContent)[] = [];

        for (const id of mediaIds) {
            const resolved = this.resolveMediaImageUrl(id);
            if ('error' in resolved) {
                return { result: resolved.error, isSuccess: false };
            }
            messageContent.push(
                { type: 'text', text: `Media id ${id}:` },
                { type: 'image_url', image_url: { url: resolved.url } },
            );
        }

        for (const { sceneId, sceneIndex } of resolvedScenes) {
            // Re-checked each time round: rendering a scene is slow, so a skip clicked
            // during a multi-scene ask should drop the rest rather than finish the batch
            // and then ask the vision model about a review the user has already waved off.
            if (isReviewSkipped(this.getState())) {
                return { result: reviewSkippedResult(), isSuccess: true };
            }
            const rendered = await this.renderScene(args, sceneId, sceneIndex);
            if ('error' in rendered) {
                return { result: rendered.error, isSuccess: false };
            }
            messageContent.push(...rendered.content);
        }

        messageContent.push({
            type: 'text',
            text: `Answer the following question about the image(s) above as specifically and concisely as possible. Do not explain your reasoning unless asked.\n\nQuestion: ${question}`,
        });

        const conversation = this.createChildConversationForToolCall(context.toolCallId, {
            systemMessage: `You are analyzing one or more images from a video. Answer the user's question about what you see.

You did not make this video and have no stake in it. Report what is actually in the frames, including problems the question did not anticipate. "Looks fine" is only an acceptable answer when you have checked each thing asked about and none of them is wrong.

Be specific and concise. When describing locations within an image, use terms like: top-left, top-center, top-right, center-left, center, center-right, bottom-left, bottom-center, bottom-right.`,
            tools: [],
            task: 'image_ask_tool',
            requiresVideoState: false,
        });

        conversation.addUserMessage(messageContent);

        try {
            const result = await conversation.executeWithLLM({
                parentActionGroup: context.parentActionGroup,
            });
            if (!result.success) {
                // A skip cancels the vision call, which surfaces here as a failure.
                if (isReviewSkipped(this.getState())) {
                    return { result: reviewSkippedResult(), isSuccess: true };
                }
                // A failed child conversation does not always carry a message; interpolating it
                // blind produced a literal "undefined" the agent could not act on.
                return {
                    result: `Error during image analysis: ${result.message || 'the vision model did not return a result. Try again in a moment.'}`,
                    isSuccess: false,
                };
            }
            return {
                isSuccess: true,
                result: result.message || 'No answer returned from image analysis.',
            };
        } catch (error) {
            if (isReviewSkipped(this.getState())) {
                return { result: reviewSkippedResult(), isSuccess: true };
            }
            return {
                result: `Error asking about images: ${error instanceof Error ? error.message : 'Unknown error'}`,
                isSuccess: false,
            };
        }
    }

    private resolveMediaImageUrl(id: string): ResolvedImage {
        const mediaRegistry = MediaRegistry.getInstance();
        const mediaData = mediaRegistry.getItem(id);
        if (!mediaData) {
            return { error: `Error: Media with ID "${id}" not found in registry.` };
        }

        const representation = mediaRegistry.getRepresentation(id);
        if (!representation) {
            return { error: `Error: Could not get representation for media ID "${id}".` };
        }

        // The "invisible" asset is a transparent placeholder used to show the
        // scene background - there's nothing meaningful to look at.
        if (
            mediaData.type === AssetContainerTypes.BACKGROUND &&
            mediaData.background?.type === SlideBackgroundModelTypeEnum.IMAGE &&
            mediaData.background?.image?.source === INVISIBLE_IMAGE_SOURCE_DATA.source &&
            mediaData.background?.image?.source_id === INVISIBLE_IMAGE_SOURCE_DATA.source_id
        ) {
            return {
                error: `Error: Media with ID "${id}" is an invisible/transparent image (effectively unset).`,
            };
        }

        if (representation.type === 'image' && mediaData.background?.image?.preview_url) {
            return { url: mediaData.background.image.preview_url };
        }
        if (representation.type === 'video' && mediaData.background?.video?.poster_url) {
            return { url: mediaData.background.video.poster_url };
        }
        const expectedField = representation.type === 'video' ? 'poster_url' : 'preview_url';
        return { error: `Error: Could not extract ${expectedField} from media ID "${id}".` };
    }

    private async renderScene(
        args: ImageAskArgs,
        sceneId: string,
        sceneIndex: number,
    ): Promise<{ content: (TextContent | ImageContent)[] } | { error: string }> {
        if (sceneIndex < 0 || sceneIndex >= this.luminary.scenes.length) {
            return {
                error: `Error: scene index ${sceneIndex} is out of bounds. Video has ${this.luminary.scenes.length} scenes.`,
            };
        }
        const scene = this.luminary.scenes[sceneIndex];
        if (!scene) {
            return { error: `Error: No scene found at index ${sceneIndex}.` };
        }

        const maxEdge = args.detail === 'high' ? SCREENSHOT_MAX_EDGE.high : SCREENSHOT_MAX_EDGE.low;
        const explicitProgress =
            typeof args.progress === 'number' && Number.isFinite(args.progress)
                ? Math.max(0, Math.min(1, args.progress))
                : undefined;

        if (args.capture_mode === undefined && explicitProgress === undefined) {
            return this.renderSingleStill(sceneId, sceneIndex, maxEdge);
        }

        const progressPoints =
            explicitProgress !== undefined
                ? [explicitProgress]
                : CAPTURE_MODE_PROGRESS[args.capture_mode as keyof typeof CAPTURE_MODE_PROGRESS];
        const frames = await captureSceneFrames(this.luminary, scene, progressPoints, maxEdge);
        if (!frames.length) {
            return {
                error: `Error: could not render scene ${sceneDisplayId(this.luminary, scene.id)}.`,
            };
        }

        const spoken = (getSceneTranscriptTokens(this.getState(), scene) ?? []).filter(
            (token) => !token.isPause,
        );
        const timed = spoken.length ? timeNarrationWords(scene, spoken) : null;

        const content: (TextContent | ImageContent)[] = [
            {
                type: 'text',
                text:
                    frames.length === 1
                        ? `Scene ${sceneId} — one frame at ${frames[0].label} of the scene's timeline:`
                        : `Scene ${sceneId} — ${frames.length} frames across the scene's timeline:`,
            },
        ];
        // Narration present but its timings can't be mapped to scene progress: attach it once as
        // plain text rather than fabricating a per-frame percentage.
        if (spoken.length && !timed) {
            content.push({
                type: 'text',
                text: formatNarration(spoken.map((token) => token.word).join(' ')),
            });
        }
        frames.forEach((frame, index) => {
            content.push({
                type: 'text',
                text: frameLabel(frame.label, progressPoints[index], timed),
            });
            content.push({ type: 'image_url', image_url: { url: frame.dataUrl } });
        });
        return { content };
    }

    private async renderSingleStill(
        sceneId: string,
        sceneIndex: number,
        maxEdge: number,
    ): Promise<{ content: (TextContent | ImageContent)[] } | { error: string }> {
        const scene = this.luminary.scenes[sceneIndex];
        let imageData: string;
        try {
            imageData = await captureSceneScreenshot(this.luminary, scene, maxEdge);
        } catch {
            return {
                error: `Error: timed out rendering scene ${sceneDisplayId(this.luminary, scene.id)}. The video preview may be busy - try again in a moment.`,
            };
        }

        const content: (TextContent | ImageContent)[] = [
            { type: 'text', text: `Scene ${sceneId}:` },
        ];
        // A single still is an overview, not a spoken instant, so attach the whole scene's timed
        // narration (empty for a text-on-media scene; plain text when timings can't be mapped).
        const narration = formatSceneNarration(scene, this.getState());
        if (narration) {
            content.push({ type: 'text', text: narration });
        }
        content.push({ type: 'image_url', image_url: { url: imageData } });
        return { content };
    }
}

/**
 * The label for a captured frame, folding in the narration spoken around its scene-progress point
 * when the scene's word timings are available: "at 50% (saying: grew **forty** percent):". Falls
 * back to the bare "at NN%:" label when there's no mappable narration.
 */
function frameLabel(label: string, progress: number, timed: TimedWord[] | null): string {
    if (!timed) {
        return `at ${label}:`;
    }
    const window = narrationWindowAt(timed, progress);
    if (!window) {
        return `at ${label}:`;
    }
    const verb = window.activeIndex >= 0 ? 'saying' : 'around';
    return `at ${label} (${verb}: ${formatNarrationWindow(window)}):`;
}
